import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { ENDPOINT, getWsUrl, isClientAbortError } from "../utils/utils";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { onError } from "@apollo/client/link/error";
import { notification } from "antd";
import { getLocaleCookie, getMessages } from "@/shared/i18n";
import { createTranslator } from "use-intl";
import { Errors } from ".";
import { GET_CURRENT_USER } from "@/application/providers/user";
import { POSSIBLE_TYPES } from "./possible-types";
import { TYPE_POLICIES } from "./type-policies";

let AFTER_ERROR = false;

export const client = new ApolloClient({
  uri: ENDPOINT,
  cache: new InMemoryCache({
    possibleTypes: POSSIBLE_TYPES,
    typePolicies: TYPE_POLICIES,
  }),
  link: ApolloLink.from([
    onError(({ graphQLErrors, networkError }) => {
      const locale = getLocaleCookie();
      const messages = getMessages(locale);
      const t = createTranslator({ locale, messages });

      if (networkError && !isClientAbortError(networkError)) {
        notification.error({
          message: networkError.message ?? t("Apollo.NETWORK_ERROR"),
        });
      }

      if (graphQLErrors) {
        graphQLErrors.forEach((error) => {
          if (error?.extensions?.classification === Errors.UNAUTHORIZED) {
            client.refetchQueries({
              include: [GET_CURRENT_USER],
            });
          } else if (
            error?.extensions?.classification === Errors.BAD_CREDENTIALS
          ) {
            notification.error({
              message: t("Apollo.BAD_CREDENTIALS"),
            });
          } else {
            notification.error({
              message: error.message,
            });
          }
        });
      }
    }),
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);

        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },

      new GraphQLWsLink(
        createClient({
          url: getWsUrl(),
          retryAttempts: Infinity,
          retryWait: (retries) =>
            new Promise((resolve) => setTimeout(resolve, retries * 3000)),
          shouldRetry: () => true,
          on: {
            error: () => {
              AFTER_ERROR = true;
            },

            connected: () => {
              if (AFTER_ERROR) {
                AFTER_ERROR = false;
                client.refetchQueries({
                  include: "active",
                });
              }
            },
          },
        }),
      ),

      new HttpLink({
        uri: `${process.env.BACKEND_ROOT}/graphql`,
      }),
    ),
  ]),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
});
